import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import BarChartIcon from '@mui/icons-material/BarChart';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { CircularProgress, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import {
  TreeItem2Checkbox,
  TreeItem2Content,
  TreeItem2GroupTransition,
  TreeItem2IconContainer,
  TreeItem2Label,
  TreeItem2Root,
} from '@mui/x-tree-view/TreeItem2';
import { TreeItem2Icon } from '@mui/x-tree-view/TreeItem2Icon';
import { unstable_useTreeItem2 as useTreeItem2, UseTreeItem2Parameters } from '@mui/x-tree-view/useTreeItem2';
import { KpiCategory } from 'benchmarking-domain/kpi-definition';
import Project from 'benchmarking-domain/project';
import ProjectSection from 'benchmarking-domain/project-section';

import KpiCategoryDetails from './kpi-category-view';
import { useKpiStructure } from './use-kpi-structure';

const CustomTreeItemContent = styled(TreeItem2Content)(({ theme }) => ({
  padding: theme.spacing(0.5, 1),
}));

export enum KpiStructureViewType {
  DetailedCharts,
  Heatmap,
}

export enum SegmentFilter {
  AllSegements,
  SameSegment,
}

interface CustomTreeItemProps
  extends Omit<UseTreeItem2Parameters, 'rootRef'>,
    Omit<React.HTMLAttributes<HTMLLIElement>, 'onFocus'> {
  hasKpis?: boolean;
  category: KpiCategory;
  onCategorySelected: (category: KpiCategory) => void;
}

const CustomTreeItem = React.forwardRef(function CustomTreeItem(
  props: CustomTreeItemProps,
  ref: React.Ref<HTMLLIElement>
) {
  const { id, itemId, label, disabled, children, category, onCategorySelected, ...other } = props;

  const hasKpis = (category: KpiCategory) => category.kpiDefinitions && category.kpiDefinitions.length > 0;
  const hasKpisMemo = useMemo(() => hasKpis(category), [category]);

  const {
    getRootProps,
    getContentProps,
    getIconContainerProps,
    getCheckboxProps,
    getLabelProps,
    getGroupTransitionProps,
    status,
  } = useTreeItem2({ id, itemId, children, label, disabled, rootRef: ref });

  return (
    <TreeItem2Root {...getRootProps({ ...other, ref })}>
      <CustomTreeItemContent {...getContentProps()}>
        <TreeItem2IconContainer {...getIconContainerProps()}>
          <TreeItem2Icon status={status} />
        </TreeItem2IconContainer>
        <TreeItem2Checkbox {...getCheckboxProps()} />
        <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
          {hasKpisMemo ? (
            <BarChartIcon aria-label="Has KPIs" sx={{ color: '#A9C472' }} />
          ) : (
            <FolderOpenIcon aria-label="No KPIs" />
          )}
          <TreeItem2Label {...getLabelProps()} />
        </Box>
      </CustomTreeItemContent>
      {children && <TreeItem2GroupTransition {...getGroupTransitionProps()} />}
    </TreeItem2Root>
  );
});

export interface KpiViewerProps {
  project: Project;
  projectSections: ProjectSection[] | undefined;
}

const KpiViewer: React.FC<KpiViewerProps> = ({ project, projectSections }) => {
  const [selectedCategory, setSelectedCategory] = useState<KpiCategory | null>(null);
  const [viewType, setViewType] = useState<KpiStructureViewType>(KpiStructureViewType.DetailedCharts);
  const [segmentFilter, setSegmentFilter] = useState<SegmentFilter>(SegmentFilter.AllSegements);

  const { kpiBenchmarks, kpiStatistics, kpiStructure, scopeKpiValues, kpiHistoryItems, initLoading } = useKpiStructure(
    project,
    projectSections
  );

  const handleCategorySelected = useCallback((category: KpiCategory) => {
    if (!category) {
      return;
    }

    setSelectedCategory(category);
  }, []);

  const renderTreeItems = useCallback(
    (category: KpiCategory, parentName: string = '') => {
      if (!category || category.hidden) {
        return;
      }

      const nodeId = parentName ? `${parentName}|${category.name}` : category.name;

      return (
        <CustomTreeItem
          key={nodeId}
          itemId={nodeId}
          label={category.name}
          category={category}
          onCategorySelected={handleCategorySelected}
        >
          {category.subCategories?.map((subcategory) => renderTreeItems(subcategory, nodeId))}
        </CustomTreeItem>
      );
    },
    [handleCategorySelected]
  );

  const handleTreeViewItemSelectionToggle = useCallback(
    (event: React.SyntheticEvent, itemId: string, isSelected: boolean) => {
      if (isSelected) {
        const path = itemId.split('|');

        if (path.length === 0) {
          return;
        }

        let categoryName = path.shift();
        let category = kpiStructure?.categories.find((cat) => cat.name === categoryName);

        if (!category) {
          return;
        }

        if (path.length === 0) {
          setSelectedCategory(category);
          return;
        }

        while (path?.length >= 1) {
          const subcategoryName = path.shift();
          category = category?.subCategories.find((cat) => cat.name === subcategoryName);
        }

        if (!category) {
          return;
        }

        setSelectedCategory(category);
      }
    },
    [kpiStructure]
  );

  const handleViewTypeChange = useCallback((event: React.MouseEvent<HTMLElement>, value: KpiStructureViewType) => {
    setViewType(value);
  }, []);

  const handleSegmentFilterChange = useCallback((event: React.MouseEvent<HTMLElement>, value: SegmentFilter) => {
    setSegmentFilter(value);
  }, []);

  if (initLoading) {
    return (
      <Stack spacing={2} paddingTop={5}>
        <Typography textAlign={'center'}>Loading data...</Typography>
      </Stack>
    );
  }

  if (!kpiStructure || !kpiStructure.categories || kpiStructure.categories.length === 0) {
    return (
      <Stack spacing={2} paddingTop={2}>
        <Typography variant="h6">No KPIs found</Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} paddingTop={2} sx={{ flex: '1 1 auto', minHeight: 0 }}>
      <Alert severity="info">
        <Typography>
          Note: KPIs are not final, and are pending completion of data input and review by Benchmarking Portal team
        </Typography>
      </Alert>

      <Stack direction="row" spacing={4} alignItems={'center'}>
        <Typography>View type: </Typography>
        <ToggleButtonGroup
          color="primary"
          value={viewType}
          exclusive
          aria-label="Platform"
          size="small"
          onChange={handleViewTypeChange}
        >
          <ToggleButton value={KpiStructureViewType.DetailedCharts} sx={{ textTransform: 'none' }}>
            Detailed Charts
          </ToggleButton>
          <ToggleButton value={KpiStructureViewType.Heatmap} sx={{ textTransform: 'none' }}>
            Heatmap
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography>Segment filter: </Typography>
        <ToggleButtonGroup
          color="primary"
          value={segmentFilter}
          exclusive
          aria-label="Platform"
          size="small"
          onChange={handleSegmentFilterChange}
        >
          <ToggleButton value={SegmentFilter.AllSegements} sx={{ textTransform: 'none' }}>
            All segments
          </ToggleButton>
          <ToggleButton value={SegmentFilter.SameSegment} sx={{ textTransform: 'none' }}>
            Same segment (Gx)
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Stack direction={'row'} spacing={4} sx={{ flex: '1 1 auto', minHeight: 0 }}>
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Typography variant="h6" paddingBottom={2} sx={{ zIndex: 1, position: 'sticky', top: 0, background: '#fff' }}>
            KPI selector
          </Typography>
          <Box sx={{ minHeight: 200 }}>
            {kpiStructure ? (
              <SimpleTreeView defaultExpandedItems={['1']} onItemSelectionToggle={handleTreeViewItemSelectionToggle}>
                {kpiStructure.categories.map((category) => renderTreeItems(category))}
              </SimpleTreeView>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Loading KPIs...
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ flex: 3, overflow: 'auto' }}>
          <Typography paddingBottom={2} variant="h6" sx={{ zIndex: 1, position: 'sticky', top: 0, background: '#fff' }}>
            KPI viewer: {selectedCategory ? `${selectedCategory.name}` : ''}
          </Typography>

          <KpiCategoryDetails
            project={project}
            scopeKpiValues={scopeKpiValues}
            kpiCategory={selectedCategory}
            viewType={viewType}
            benchmarks={kpiBenchmarks}
            statistics={kpiStatistics}
            historyItems={kpiHistoryItems}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

export default KpiViewer;
